import { Typography } from '@/components/atoms/Typography';
import {
  BuildingIcon,
  ChevronRightIcon,
  DirhamIcon,
  ProofOfMortgageIcon,
} from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { hasNumericValue } from '@/core/utils/fieldAvailability';
import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import type { CompletePaymentMethod } from '../hooks/useCompletePaymentFlow';
import {
  PaymentFlowMobilePairFooter,
  PaymentFlowMobileScreen,
  useIsPaymentPhoneLayout,
} from './PaymentFlowMobileScreen';

const DIALOG_WIDTH = 600;
const DIALOG_HEIGHT = 554;
/** Figma Font-Error / Outstanding amount */
const OUTSTANDING_AMOUNT_COLOR = UI_PRIMITIVES.redLight;
/** Figma selected payment method card fill */
const SELECTED_METHOD_BG = '#F8F8F8';

export interface CompletePaymentDialogProps {
  visible: boolean;
  onClose: () => void;
  onContinue: () => void;
  totalPrice: number;
  iskanLoanAmount: number;
  outstandingBalance: number;
  selectedMethod: CompletePaymentMethod | null;
  onSelectMethod: (method: CompletePaymentMethod) => void;
  canContinue: boolean;
}

type PaymentColors = ReturnType<typeof useUIColors>;

function InlineDirhamAmount({
  amount,
  color,
  compact,
}: Readonly<{ amount: number; color: string; compact: boolean }>) {
  return (
    <View style={styles.inlineAmount}>
      <DirhamIcon size={compact ? 10 : 11} color={color} />
      <Typography
        variant="caption"
        color={color}
        style={[styles.metaText, compact ? styles.metaTextCompact : null]}
      >
        {amount.toLocaleString()}
      </Typography>
    </View>
  );
}

function PaymentBalanceCard({
  labels,
  colors,
  outstandingBalance,
  totalPrice,
  iskanLoanAmount,
  surfaceColor,
  compact,
}: Readonly<{
  labels: UnitDetailsLabels;
  colors: PaymentColors;
  outstandingBalance: number;
  totalPrice: number;
  iskanLoanAmount: number;
  surfaceColor: string;
  compact: boolean;
}>) {
  const showTotal = hasNumericValue(totalPrice);
  const showLoan = Number.isFinite(iskanLoanAmount);

  return (
    <View
      style={[
        styles.balanceCard,
        compact ? styles.balanceCardCompact : styles.balanceCardDesktop,
        {
          backgroundColor: surfaceColor,
          borderColor: colors.outlineDisabled,
        },
      ]}
    >
      <Typography
        variant="footnote"
        color="secondary"
        style={[styles.balanceLabel, compact ? styles.balanceLabelCompact : null]}
      >
        {labels.OUTSTANDING_BALANCE}
      </Typography>
      <View style={styles.balanceAmountRow}>
        <DirhamIcon size={compact ? 16 : 18} color={OUTSTANDING_AMOUNT_COLOR} />
        <Typography
          variant="title2"
          color={OUTSTANDING_AMOUNT_COLOR}
          style={[
            styles.balanceAmount,
            compact ? styles.balanceAmountCompact : null,
          ]}
        >
          {outstandingBalance.toLocaleString()}
        </Typography>
      </View>
      <View style={styles.metaRow}>
        {showTotal ? (
          <View style={styles.metaItem}>
            <Typography
              variant="caption"
              color="secondary"
              style={[styles.metaText, compact ? styles.metaTextCompact : null]}
            >
              {labels.TOTAL_UNIT_PRICE}:
            </Typography>
            <InlineDirhamAmount
              amount={totalPrice}
              color={colors.fontSecondary}
              compact={compact}
            />
          </View>
        ) : null}
        {showTotal && showLoan ? (
          <Typography
            variant="caption"
            color="secondary"
            style={[styles.metaText, compact ? styles.metaTextCompact : null]}
          >
            {' • '}
          </Typography>
        ) : null}
        {showLoan ? (
          <View style={styles.metaItem}>
            <Typography
              variant="caption"
              color="secondary"
              style={[styles.metaText, compact ? styles.metaTextCompact : null]}
            >
              {labels.APPROVED_ISKAN_LOAN}:
            </Typography>
            <InlineDirhamAmount
              amount={iskanLoanAmount}
              color={colors.fontSecondary}
              compact={compact}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
}

function PaymentMethodRow({
  title,
  description,
  selected,
  onPress,
  icon,
  colors,
  isRTL,
  tall,
  compact,
}: Readonly<{
  title: string;
  description: string;
  selected: boolean;
  onPress: () => void;
  icon: React.ReactNode;
  colors: PaymentColors;
  isRTL: boolean;
  tall?: boolean;
  compact: boolean;
}>) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={title}
      style={[
        styles.methodRow,
        tall ? styles.methodRowTall : null,
        compact ? styles.methodRowCompact : null,
        {
          borderColor: selected
            ? colors.fontHighContrast
            : colors.outlineDisabled,
          backgroundColor: selected
            ? SELECTED_METHOD_BG
            : colors.backgroundLevel1,
        },
        selected && styles.methodRowSelected,
      ]}
    >
      <View
        style={[
          styles.methodIconWrap,
          compact ? styles.methodIconWrapCompact : null,
          {
            backgroundColor: selected
              ? colors.backgroundLevel1
              : colors.backgroundLevel2,
          },
        ]}
      >
        {icon}
      </View>
      <View style={styles.methodCopy}>
        <Typography
          variant="headline"
          color="highContrast"
          style={styles.methodTitle}
        >
          {title}
        </Typography>
        <Typography
          variant="callout"
          color="secondary"
          style={styles.methodDescription}
        >
          {description}
        </Typography>
      </View>
      <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
        <ChevronRightIcon size={compact ? 12 : 14} color={colors.fontTertiary} />
      </View>
    </Pressable>
  );
}

function selectPaymentMethod(
  method: CompletePaymentMethod,
  onSelectMethod: (method: CompletePaymentMethod) => void,
): void {
  trackCatalogEvent({
    screen: ALMETLAA_SCREEN.TOP_UP,
    actionName:
      method === 'bank-transfer'
        ? ALMETLAA_ACTION.TOP_UP.BANK_TRANSFER_SELECT_CLICK
        : ALMETLAA_ACTION.TOP_UP.PROOF_OF_MORTGAGE_SELECT_CLICK,
    eventType: 'action',
  });
  onSelectMethod(method);
}

function CompletePaymentBody({
  labels,
  colors,
  isRTL,
  outstandingBalance,
  totalPrice,
  iskanLoanAmount,
  selectedMethod,
  onSelectMethod,
  balanceSurface,
  compact,
}: Readonly<{
  labels: UnitDetailsLabels;
  colors: PaymentColors;
  isRTL: boolean;
  outstandingBalance: number;
  totalPrice: number;
  iskanLoanAmount: number;
  selectedMethod: CompletePaymentMethod | null;
  onSelectMethod: (method: CompletePaymentMethod) => void;
  balanceSurface: string;
  compact: boolean;
}>) {
  const iconColor = colors.fontHighContrast;
  const iconSize = compact ? 18 : 20;
  return (
    <View style={[styles.body, compact ? styles.bodyCompact : null]}>
      <PaymentBalanceCard
        labels={labels}
        colors={colors}
        outstandingBalance={outstandingBalance}
        totalPrice={totalPrice}
        iskanLoanAmount={iskanLoanAmount}
        surfaceColor={balanceSurface}
        compact={compact}
      />
      <Typography
        variant="headline"
        color="#1A1A1A"
        style={[styles.proceedTitle, compact ? styles.proceedTitleCompact : null]}
      >
        {labels.HOW_TO_PROCEED}
      </Typography>
      <View style={styles.methods}>
        <PaymentMethodRow
          title={labels.BANK_TRANSFER}
          description={labels.BANK_TRANSFER_DESCRIPTION}
          selected={selectedMethod === 'bank-transfer'}
          onPress={() => selectPaymentMethod('bank-transfer', onSelectMethod)}
          colors={colors}
          isRTL={isRTL}
          compact={compact}
          icon={<BuildingIcon size={iconSize} color={iconColor} />}
        />
        <PaymentMethodRow
          title={labels.PROOF_OF_MORTGAGE}
          description={labels.PROOF_OF_MORTGAGE_DESCRIPTION}
          selected={selectedMethod === 'proof-of-mortgage'}
          onPress={() => selectPaymentMethod('proof-of-mortgage', onSelectMethod)}
          colors={colors}
          isRTL={isRTL}
          compact={compact}
          tall={!compact}
          icon={<ProofOfMortgageIcon size={iconSize} color={iconColor} />}
        />
      </View>
    </View>
  );
}

/**
 * Pay Now — choose Bank Transfer or Proof of Mortgage.
 * Continue is enabled once a method is selected.
 */
export function CompletePaymentDialog({
  visible,
  onClose,
  onContinue,
  totalPrice,
  iskanLoanAmount,
  outstandingBalance,
  selectedMethod,
  onSelectMethod,
  canContinue,
}: Readonly<CompletePaymentDialogProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const isPhone = useIsPaymentPhoneLayout();

  React.useEffect(() => {
    if (!visible) {
      return;
    }
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: isPhone
        ? ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_SHEET_OPEN
        : ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_DIALOG_VIEW,
      eventType: 'view',
    });
  }, [visible, isPhone]);

  const handleContinue = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_DIALOG_CONFIRM,
      eventType: 'action',
    });
    onContinue();
  };
  const handleCancel = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_DIALOG_CANCEL,
      eventType: 'action',
    });
    onClose();
  };
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const body = (
    <CompletePaymentBody
      labels={labels}
      colors={colors}
      isRTL={isRTL}
      outstandingBalance={outstandingBalance}
      totalPrice={totalPrice}
      iskanLoanAmount={iskanLoanAmount}
      selectedMethod={selectedMethod}
      onSelectMethod={onSelectMethod}
      balanceSurface={
        isPhone ? colors.backgroundLevel1 : colors.backgroundLevel2
      }
      compact={isPhone}
    />
  );

  if (isPhone) {
    return (
      <PaymentFlowMobileScreen
        visible={visible}
        title={labels.COMPLETE_PAYMENT_TITLE}
        onBack={onClose}
        backAccessibilityLabel={labels.CANCEL_RESERVATION_BUTTON}
        footer={
          <PaymentFlowMobilePairFooter
            secondaryLabel={labels.CANCEL_RESERVATION_BUTTON}
            primaryLabel={labels.CONTINUE}
            onSecondary={handleCancel}
            onPrimary={handleContinue}
            primaryDisabled={!canContinue}
          />
        }
      >
        {body}
      </PaymentFlowMobileScreen>
    );
  }

  return (
    <ContentDialog
      visible={visible}
      onClose={onClose}
      contentAlign="left"
      maxWidth={DIALOG_WIDTH}
      title={labels.COMPLETE_PAYMENT_TITLE}
      titleVariant="title1"
      titleColor="highContrast"
      titleStyle={styles.title}
      headerStyle={styles.header}
      cardStyle={[
        styles.card,
        styles.cardDesktop,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
      actionsStyle={styles.actions}
      closeOnBackdropPress
      actions={[
        {
          label: labels.CANCEL_RESERVATION_BUTTON,
          variant: 'text',
          flex: false,
          fullWidth: false,
          onPress: handleCancel,
          style: styles.cancelButton,
          textStyle: [styles.actionLabel, styles.cancelLink],
        },
        {
          label: labels.CONTINUE,
          variant: 'primary',
          flex: false,
          fullWidth: false,
          disabled: !canContinue,
          onPress: handleContinue,
          style: styles.continueButton,
          textStyle: styles.actionLabel,
        },
      ]}
    >
      {body}
    </ContentDialog>
  );
}

const styles = StyleSheet.create({
  /** Figma: 600×554, radius 16, padding 32 — tighter title→body so balance sits higher */
  card: {
    width: '100%',
    borderRadius: 16,
    paddingTop: 32,
    paddingRight: 32,
    paddingBottom: 32,
    paddingLeft: 32,
    gap: 16,
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  cardDesktop: {
    width: DIALOG_WIDTH,
    minWidth: DIALOG_WIDTH,
    maxWidth: DIALOG_WIDTH,
    height: DIALOG_HEIGHT,
    minHeight: DIALOG_HEIGHT,
    maxHeight: DIALOG_HEIGHT,
    flexShrink: 0,
    justifyContent: 'space-between',
  },
  header: {
    gap: 0,
  },
  /** Figma Font/Size/2XL Medium 28, LH 100%, #1A1A1A */
  title: {
    fontFamily: 'Matter-Medium',
    // Avoid synthetic bold on web (face + weight = blurry)
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 28,
    lineHeight: 28,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  body: {
    gap: 16,
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    justifyContent: 'flex-start',
  },
  /** Figma: 536×109 — equal vertical rhythm between label / amount / meta */
  balanceCard: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    justifyContent: 'space-between',
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  balanceCardDesktop: {
    height: 109,
  },
  /** Figma Font/Size/2XS Regular 13, LH 100% */
  balanceLabel: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 13,
    lineHeight: 13,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  /** Phone: Font/Size/3XS Regular 12 */
  balanceLabelCompact: {
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
  },
  balanceAmountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  /** Figma Font/Size/XL Medium 22, LH 100%, #922A22 */
  balanceAmount: {
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 22,
    lineHeight: 22,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  /** Phone: Font/Size/L Medium 20, LH 100% */
  balanceAmountCompact: {
    fontSize: 20,
    lineHeight: 20,
  },
  balanceCardCompact: {
    minHeight: 88,
    padding: 16,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  inlineAmount: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  /** Figma Font/Size/3XS Regular 12, secondary #4D4D4D */
  metaText: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 12,
  },
  /** Phone: 10 Regular, LH 100%, #4D4D4D */
  metaTextCompact: {
    fontSize: 10,
    lineHeight: 10,
    color: '#4D4D4D',
  },
  bodyCompact: {
    gap: 12,
    flexGrow: 0,
  },
  /** Figma Font/Size/Base SemiBold 16 — extra top margin after balance card */
  proceedTitle: {
    marginTop: 12,
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 16,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  /** Phone: Font/Size/XS Medium 14, LH 100%, #1A1A1A */
  proceedTitleCompact: {
    marginTop: 4,
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 14,
    lineHeight: 14,
  },
  methods: {
    gap: 12,
  },
  /** Figma: 536×80 / 536×84, gap 16, radius 12, border 1, padding 20 */
  methodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minHeight: 80,
    gap: 16,
    borderWidth: 1,
    borderRadius: 12,
    padding: 20,
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  methodRowCompact: {
    minHeight: 72,
    height: 'auto',
    padding: 16,
    gap: 12,
    alignItems: 'center',
  },
  methodRowSelected: {
    borderWidth: 1.5,
  },
  /** Proof of Mortgage row is slightly taller in Figma (84 vs 80) */
  methodRowTall: {
    minHeight: 84,
  },
  methodIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  methodIconWrapCompact: {
    width: 36,
    height: 36,
  },
  methodCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  /** Figma Font/Size/Base SemiBold 16, LH 100%, letter-spacing 0 */
  methodTitle: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
    includeFontPadding: false,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  /** Figma Font/Size/S Regular 15, LH 100%, letter-spacing 0, Font-Secondary */
  methodDescription: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 15,
    lineHeight: 15,
    letterSpacing: 0,
    includeFontPadding: false,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  actions: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 0,
    marginTop: 0,
  },
  cancelButton: {
    height: 40,
    paddingHorizontal: 0,
    overflow: 'visible',
  },
  continueButton: {
    width: 93,
    height: 48,
    minWidth: 93,
    gap: 4,
    borderRadius: 4,
    paddingTop: 2,
    paddingRight: 2,
    paddingBottom: 2,
    paddingLeft: 2,
  },
  /** Cancel + Continue — Base SemiBold 16, LH 100%, center */
  actionLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  cancelLink: {
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    paddingBottom: 2,
    ...(Platform.OS === 'web'
      ? ({ textUnderlineOffset: 3 } as object)
      : null),
  },
});

export default CompletePaymentDialog;
